import { motion } from "framer-motion";
import { useScrollAnimation } from "../../utils/useScrollAnimation";
import { LoadingButton } from "../LoadingButton";

// Define the types for our props
interface Feature {
  title: string;
  description: string;
}

interface FeatureCategoryProps {
  id: string;
  title: string;
  description: string;
  image: string;
  features: Feature[];
  isReversed?: boolean;
  index: number;
}

function FeatureCategory({ 
  id, 
  title, 
  description, 
  image, 
  features, 
  isReversed = false,
  index
}: FeatureCategoryProps) {
  const { ref, isInView } = useScrollAnimation();
  
  return (
    <section 
      id={id}
      ref={ref}
      className={`py-20 px-4 ${index % 2 === 1 ? 'bg-[#fff5eb]' : 'bg-white'}`}
    >
      <div className="container mx-auto max-w-6xl">
        <div className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12`}>
          {/* Image Section */}
          <motion.div 
            className="flex-1"
            initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="relative rounded-xl overflow-hidden shadow-xl">
              <img 
                src={image} 
                alt={title} 
                className="w-full h-auto rounded-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#694e42]/20 to-transparent"></div>
              
              {/* Floating indicator */}
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm py-2 px-4 rounded-full shadow-md">
                <span className="text-[#ff8c42] font-medium">{title}</span>
              </div>
            </div>
          </motion.div>
          
          {/* Content Section */}
          <div className="flex-1">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-4 text-[#694e42]">{title}</h2>
              <p className="text-lg text-[#8a7266] mb-8">{description}</p>
            </motion.div>
            
            <div className="space-y-6">
              {features.map((feature, featureIndex) => (
                <motion.div 
                  key={featureIndex}
                  className="flex gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + featureIndex * 0.1 }}
                >
                  <div className="mt-1 bg-[#ff8c42]/10 p-2 rounded-full h-min">
                    <svg className="w-5 h-5 text-[#ff8c42]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#694e42] mb-1">{feature.title}</h3>
                    <p className="text-[#8a7266]">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <LoadingButton 
                className="px-6 py-2 bg-[#ff8c42] text-white rounded-md font-medium"
              >
                Try {title} Now
              </LoadingButton>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FeatureCategory;